import React, { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate, Navigate, Link } from "react-router-dom";
import { api } from "../lib/api";
import { useApp } from "../contexts/AppContext";
import { useAuth } from "../contexts/AuthContext";
import { listGiftCardsByOrder } from "../services/giftCards";
import {
  Check, Gift, Copy, CheckCircle2, Package, Search, X,
  Truck, MapPin, CreditCard, ShoppingBag, Clock, MessageSquare,
} from "lucide-react";

// ────────────────────────────────────────────────────────────────
// Helpers
// ────────────────────────────────────────────────────────────────

// Bəzi tərcümələr UPPERCASE saxlanılır (ümumi sayt stil seçimi). Bu səhifədə
// "yalnız baş hərflər böyük" üslubu istənir, ona görə tək UPPERCASE sözləri
// avtomatik olaraq Title Case-ə çeviririk (digər tərcümələri toxunmadan).
function tc(value) {
  if (!value || typeof value !== "string") return value || "";
  const trimmed = value.trim();
  if (!trimmed) return value;
  // Yalnız tamamı böyük hərflərdənsə Title Case-ə çevir
  if (trimmed === trimmed.toUpperCase() && /[A-ZƏĞIİÖŞÇÜА-Я]/.test(trimmed)) {
    return trimmed
      .toLocaleLowerCase()
      .split(/\s+/)
      .map((w) => (w ? w[0].toLocaleUpperCase() + w.slice(1) : w))
      .join(" ");
  }
  return value;
}

// İlk hərfi böyüdür, qalanı toxunmadan saxlayır (label-lar üçün)
function sc(value) {
  const v = tc(value);
  if (!v) return v;
  return v.charAt(0).toLocaleUpperCase() + v.slice(1);
}

// Çatdırılma axını (yalnız fiziki məhsullar üçün)
const FLOW = ["placed", "preparing", "with_courier", "on_the_way", "delivered"];

// Status mövzu rəngləri
const STATUS_THEME = {
  placed:       { dot: "var(--primary)",   text: "text-[var(--primary)]" },
  preparing:    { dot: "rgb(139,92,246)",  text: "text-violet-500"       },
  with_courier: { dot: "rgb(6,182,212)",   text: "text-cyan-500"         },
  on_the_way:   { dot: "rgb(20,184,166)",  text: "text-teal-500"         },
  delivered:    { dot: "rgb(16,185,129)",  text: "text-emerald-500"      },
  cancelled:    { dot: "var(--accent)",    text: "text-[var(--accent)]"  },
};

const SORT_OPTIONS = [
  ["date_desc",  { az: "Tarix (ən yeni)",  en: "Date (newest)",    ru: "Дата (новые)"  }],
  ["date_asc",   { az: "Tarix (ən köhnə)", en: "Date (oldest)",    ru: "Дата (старые)" }],
  ["total_desc", { az: "Məbləğ (çox→az)",  en: "Total (high→low)", ru: "Сумма (выше)"  }],
  ["total_asc",  { az: "Məbləğ (az→çox)",  en: "Total (low→high)", ru: "Сумма (ниже)"  }],
];

// Səhifə üçün xüsusi kiçik etiket stili (UPPERCASE deyil — yalnız incə kapital
// ilk hərflər). Sayt boyu mövcud `metadata` sinfi UPPERCASE-dir, ona görə
// burada onu istifadə etmirik.
const labelCls = "text-[11px] tracking-[0.04em] text-[var(--text-muted)] font-medium";

function OrderCard({ order, t, formatPrice }) {
  const [giftCards, setGiftCards] = useState([]);
  const [copied, setCopied] = useState("");

  useEffect(() => {
    listGiftCardsByOrder(order.id).then(setGiftCards).catch(() => setGiftCards([]));
  }, [order.id]);

  const copy = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(text);
      setTimeout(() => setCopied(""), 1500);
    } catch { /* ignore */ }
  };

  const idx = FLOW.indexOf(order.status);
  const labels = {
    placed:       sc(t.checkout.placed),
    preparing:    sc(t.checkout.preparing),
    with_courier: sc(t.checkout.withCourier),
    on_the_way:   sc(t.checkout.onTheWay),
    delivered:    sc(t.checkout.delivered),
    cancelled:    "Ləğv edilib",
  };
  const isDigitalOnly = order.delivery_code === "digital";
  const isCancelled   = order.status === "cancelled";
  const theme = STATUS_THEME[order.status] || STATUS_THEME.placed;

  const created = order.created_at ? new Date(order.created_at) : null;
  const dateStr = created
    ? created.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" })
    : "";
  const timeStr = created
    ? created.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" })
    : "";

  return (
    <article
      className="border border-[var(--border)] bg-[var(--bg)]"
      data-testid={`order-card-${order.tracking_no}`}
    >
      {/* ── Üst başlıq ─────────────────────────────────────────── */}
      <header className="flex flex-wrap items-start justify-between gap-4 px-6 py-5 border-b border-[var(--border-soft)]">
        <div className="flex items-center gap-3 min-w-0">
          <span
            className="w-2.5 h-2.5 rounded-full shrink-0"
            style={{ background: theme.dot, boxShadow: `0 0 0 4px ${theme.dot}22` }}
            aria-hidden="true"
          />
          <div className="min-w-0">
            <div className={labelCls}>Sifariş №</div>
            <div className="font-mono text-[15px] mt-0.5 truncate">{order.tracking_no}</div>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-6">
          <div className="text-right">
            <div className={labelCls}>Tarix</div>
            <div className="font-mono text-[13px] mt-0.5 text-[var(--text-2)]">
              {dateStr}{timeStr && ` · ${timeStr}`}
            </div>
          </div>
          <div className="text-right">
            <div className={labelCls}>Status</div>
            <div className={`font-display text-[15px] mt-0.5 ${theme.text}`}>
              {labels[order.status] || order.status}
            </div>
          </div>
          <div className="text-right">
            <div className={labelCls}>Cəmi</div>
            <div className="font-display text-xl mt-0.5">{formatPrice(order.total_azn)}</div>
          </div>
        </div>
      </header>

      <div className="p-6 space-y-7">
        {/* ── Məhsullar ─────────────────────────────────────────── */}
        <section>
          <div className={`${labelCls} mb-3`}>Məhsullar</div>
          <ul className="divide-y divide-[var(--border-soft)] border-y border-[var(--border-soft)]">
            {(order.items || []).map((i, k) => (
              <li key={k} className="flex gap-4 py-3 items-center">
                {i.is_gift_card && !i.cover_url ? (
                  <div className="w-20 h-20 sm:w-24 sm:h-24 border border-[var(--border-soft)] bg-[var(--surface)] flex items-center justify-center text-[var(--primary)] shrink-0">
                    <Gift size={28} strokeWidth={1.3} />
                  </div>
                ) : (
                  <img
                    src={i.cover_url}
                    alt={i.title}
                    className="w-20 h-20 sm:w-24 sm:h-24 object-cover border border-[var(--border-soft)] shrink-0 bg-[var(--surface)]"
                    loading="lazy"
                  />
                )}
                <div className="flex-1 min-w-0">
                  <div className="font-display text-base sm:text-lg flex items-center gap-2 truncate">
                    {i.is_gift_card && <Gift size={13} className="text-[var(--primary)] shrink-0" />}
                    <span className="truncate">{i.title}</span>
                  </div>
                  {i.artist_name && (
                    <div className="text-sm text-[var(--text-2)] mt-0.5 truncate">{i.artist_name}</div>
                  )}
                  <div className="text-xs text-[var(--text-muted)] font-mono mt-1">× {i.qty}</div>
                </div>
                <div className="font-mono text-sm sm:text-base shrink-0 self-center">
                  {formatPrice(i.price_azn * i.qty)}
                </div>
              </li>
            ))}
          </ul>
        </section>

        {/* ── Hədiyyə kartı kodları ──────────────────────────────── */}
        {giftCards.length > 0 && (
          <section className="border border-[var(--primary)] bg-[var(--surface)] p-5">
            <div className="flex items-center gap-2 text-[var(--primary)] font-display text-base mb-3">
              <Gift size={16} /> Hədiyyə kart kodları
            </div>
            <div className="space-y-2">
              {giftCards.map((gc) => (
                <div key={gc.id} className="flex flex-wrap justify-between items-center gap-3 bg-[var(--bg)] border border-[var(--border-soft)] px-4 py-3">
                  <div>
                    <div className="font-mono text-[var(--primary)] text-base tracking-wider">{gc.code}</div>
                    <div className="text-xs text-[var(--text-muted)] mt-0.5">
                      {gc.name || "Hədiyyə Kartı"} · {formatPrice(Number(gc.initial_amount_azn) || 0)}
                      {gc.recipient_name ? ` · → ${gc.recipient_name}` : ""}
                    </div>
                  </div>
                  <button
                    onClick={() => copy(gc.code)}
                    className="flex items-center gap-1 text-xs text-[var(--text-2)] hover:text-[var(--primary)]"
                    type="button"
                  >
                    {copied === gc.code
                      ? <><CheckCircle2 size={14} className="text-[var(--primary)]" /> <span>Kopyalandı</span></>
                      : <><Copy size={14} /> <span>Kopyala</span></>}
                  </button>
                </div>
              ))}
            </div>
            <Link to="/my-gift-cards" className="btn-outline mt-4 inline-flex" style={{ textTransform: "none", letterSpacing: "0" }}>
              Hədiyyə kartlarıma bax
            </Link>
          </section>
        )}

        {/* ── Çatdırılma cədvəli ─────────────────────────────────── */}
        {!isDigitalOnly && !isCancelled && (
          <section>
            <div className={`${labelCls} mb-4 flex items-center gap-2`}>
              <Truck size={12} /> Çatdırılma statusu
            </div>
            <div className="relative">
              <div className="absolute left-0 right-0 top-[5px] h-px bg-[var(--border-soft)]" />
              <div
                className="absolute left-0 top-[5px] h-px bg-[var(--primary)] transition-all"
                style={{ width: idx >= 0 ? `${(idx / (FLOW.length - 1)) * 100}%` : "0%" }}
              />
              <div className="grid grid-cols-5 gap-2 relative">
                {FLOW.map((s, i) => (
                  <div key={s} className="flex flex-col items-center text-center" data-testid={`step-${s}`}>
                    <div className={`timeline-dot ${i < idx ? "done" : i === idx ? "active" : ""}`}>
                      {i <= idx && (
                        <Check
                          size={10}
                          className="text-[var(--inverse)] m-auto"
                          style={{ display: "block", marginTop: "1px" }}
                        />
                      )}
                    </div>
                    <div className={`text-[11px] mt-3 leading-tight ${i <= idx ? "text-[var(--primary)] font-display" : "text-[var(--text-muted)]"}`}>
                      {labels[s]}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* ── Admin tərəfindən yazılmış müştəri qeydi ──────────── */}
        {order.customer_visible_note && (
          <section className="border border-[var(--primary)] bg-[var(--surface)]/40 p-4">
            <div className="flex items-center gap-2 text-[var(--primary)] font-display text-sm mb-2">
              <MessageSquare size={14} /> Mağazadan qeyd
            </div>
            <p className="text-sm text-[var(--text)] whitespace-pre-wrap leading-relaxed">
              {order.customer_visible_note}
            </p>
          </section>
        )}

        {/* ── Çatdırılma + ödəniş info ────────────────────────── */}
        <section className="grid sm:grid-cols-2 gap-4">
          {!isDigitalOnly && (order.address || order.city || order.full_name) && (
            <div className="border border-[var(--border-soft)] p-4">
              <div className={`${labelCls} mb-2 flex items-center gap-2`}>
                <MapPin size={11} /> Çatdırılma ünvanı
              </div>
              <div className="text-sm">
                <div className="font-display text-base">{order.full_name || "—"}</div>
                {order.phone && <div className="text-[var(--text-2)] mt-0.5">{order.phone}</div>}
                {order.address && <div className="text-[var(--text-2)] mt-1">{order.address}</div>}
                {order.city && <div className="text-[var(--text-2)]">{order.city}</div>}
              </div>
            </div>
          )}

          <div className="border border-[var(--border-soft)] p-4">
            <div className={`${labelCls} mb-2 flex items-center gap-2`}>
              <CreditCard size={11} /> Ödəniş
            </div>
            <div className="text-sm space-y-1">
              <div className="flex justify-between">
                <span className="text-[var(--text-2)]">{sc(t.common.subtotal)}</span>
                <span className="font-mono">{formatPrice(order.subtotal_azn || 0)}</span>
              </div>
              {Number(order.discount_azn || 0) > 0 && (
                <div className="flex justify-between text-[var(--accent)]">
                  <span>{sc(t.coupon?.discount || "Endirim")}{order.coupon_code ? ` (${order.coupon_code})` : ""}</span>
                  <span className="font-mono">−{formatPrice(order.discount_azn)}</span>
                </div>
              )}
              <div className="flex justify-between">
                <span className="text-[var(--text-2)]">{sc(t.common.delivery)}</span>
                <span className="font-mono">
                  {Number(order.delivery_price_azn || 0) === 0
                    ? (sc(t.common.free) || "Pulsuz")
                    : formatPrice(order.delivery_price_azn)}
                </span>
              </div>
              <div className="flex justify-between border-t border-[var(--border-soft)] pt-1.5 mt-1.5">
                <span className="font-display">{sc(t.common.total)}</span>
                <span className="font-mono font-display">{formatPrice(order.total_azn)}</span>
              </div>
              {order.payment?.transaction && (
                <div className="text-[10px] text-[var(--text-muted)] mt-2 pt-2 border-t border-[var(--border-soft)] truncate font-mono">
                  Əməliyyat: {order.payment.transaction}
                </div>
              )}
            </div>
          </div>
        </section>

        {/* ── Əməliyyatlar ───────────────────────────────────────── */}
        <footer className="flex flex-wrap items-center gap-3 pt-2 border-t border-[var(--border-soft)]">
          <button
            onClick={() => copy(order.tracking_no)}
            className="text-xs text-[var(--text-2)] hover:text-[var(--primary)] flex items-center gap-1"
            type="button"
          >
            {copied === order.tracking_no
              ? <><CheckCircle2 size={12} /> <span>İzləmə nömrəsi kopyalandı</span></>
              : <><Copy size={12} /> <span>İzləmə nömrəsini kopyala</span></>}
          </button>
        </footer>
      </div>
    </article>
  );
}

export default function MyOrdersPage() {
  const { t, formatPrice, lang } = useApp();
  const { user, authChecked } = useAuth();
  const { tracking_no } = useParams();
  const nav = useNavigate();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  const [statusFilter, setStatusFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [sortKey, setSortKey] = useState("date_desc");

  useEffect(() => {
    if (!authChecked) return;
    if (!user) { setLoading(false); return; }
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/orders/mine");
        if (!cancelled) setOrders(Array.isArray(data) ? data : []);
      } catch {
        if (!cancelled) setOrders([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [authChecked, user]);

  const counts = useMemo(() => {
    const m = { all: orders.length };
    for (const o of orders) m[o.status] = (m[o.status] || 0) + 1;
    return m;
  }, [orders]);

  const visibleOrders = useMemo(() => {
    let arr = orders.slice();
    if (statusFilter !== "all") arr = arr.filter((o) => o.status === statusFilter);
    const term = search.trim().toLowerCase();
    if (term) {
      arr = arr.filter((o) => {
        const hay = [
          o.tracking_no,
          ...(o.items || []).flatMap((i) => [i.title, i.artist_name]),
        ].filter(Boolean).join(" ").toLowerCase();
        return hay.includes(term);
      });
    }
    arr.sort((a, b) => {
      switch (sortKey) {
        case "date_asc":   return (a.created_at || "").localeCompare(b.created_at || "");
        case "total_desc": return Number(b.total_azn || 0) - Number(a.total_azn || 0);
        case "total_asc":  return Number(a.total_azn || 0) - Number(b.total_azn || 0);
        case "date_desc":
        default:           return (b.created_at || "").localeCompare(a.created_at || "");
      }
    });

    if (tracking_no) {
      const i = arr.findIndex((o) => o.tracking_no === tracking_no);
      if (i > 0) {
        const [m] = arr.splice(i, 1);
        arr.unshift(m);
      }
    }
    return arr;
  }, [orders, statusFilter, search, sortKey, tracking_no]);

  const stats = useMemo(() => {
    const totalSpent = orders.reduce((s, o) => s + Number(o.total_azn || 0), 0);
    const inProgress = orders.filter((o) =>
      ["placed", "preparing", "with_courier", "on_the_way"].includes(o.status)
    ).length;
    const delivered = orders.filter((o) => o.status === "delivered").length;
    return { totalSpent, inProgress, delivered, count: orders.length };
  }, [orders]);

  if (!authChecked) {
    return <div className="min-h-[60vh] flex items-center justify-center text-[var(--text-2)]">{sc(t.common.loading)}</div>;
  }
  if (!user) {
    const redirect = tracking_no ? `/orders/${tracking_no}` : "/orders";
    return <Navigate to={`/login?redirect=${encodeURIComponent(redirect)}`} replace />;
  }

  // Status filtri çipləri üçün
  const STATUS_CHIPS = [
    ["placed",       sc(t.checkout.placed)],
    ["preparing",    sc(t.checkout.preparing)],
    ["with_courier", sc(t.checkout.withCourier)],
    ["on_the_way",   sc(t.checkout.onTheWay)],
    ["delivered",    sc(t.checkout.delivered)],
    ["cancelled",    "Ləğv edilib"],
  ];

  const sortLabel = (key) => {
    const found = SORT_OPTIONS.find(([k]) => k === key);
    if (!found) return "";
    return found[1][lang] || found[1].az;
  };

  const titleMyOrders = tc(t.profile?.myOrders) || "Sifarişlərim";
  const subtitle = tc(t.profile?.myOrdersDesc) || "Çatdırılmaları və tarixçəni izləyin";

  return (
    <div className="max-w-6xl mx-auto px-6 md:px-12 py-16" data-testid="my-orders-page">
      {/* Başlıq */}
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className={labelCls}>Hesabım</div>
          <h1 className="font-display text-4xl md:text-5xl lg:text-[56px] mt-3 leading-[1.04]" style={{ textTransform: "none", letterSpacing: "-0.02em" }}>
            {titleMyOrders}
          </h1>
          {orders.length > 0 && (
            <p className="text-[var(--text-2)] mt-3 max-w-xl">{subtitle}</p>
          )}
        </div>
      </div>

      {/* ── Statistika kartları ─────────────────────────────────── */}
      {!loading && orders.length > 0 && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-8">
          <StatBlock icon={ShoppingBag} label="Ümumi sifariş" value={stats.count} />
          <StatBlock icon={CreditCard} label="Ümumi xərc" value={formatPrice(stats.totalSpent)} mono />
          <StatBlock
            icon={Clock}
            label="Aktiv sifariş"
            value={stats.inProgress}
            accent={stats.inProgress > 0}
          />
          <StatBlock icon={CheckCircle2} label="Çatdırıldı" value={stats.delivered} />
        </div>
      )}

      {/* ── Filtr paneli ────────────────────────────────────────── */}
      {!loading && orders.length > 0 && (
        <div className="mt-8 border border-[var(--border)] bg-[var(--surface)]/30 p-4 space-y-3">
          <div className="flex flex-wrap items-center gap-2">
            <button
              onClick={() => setStatusFilter("all")}
              className={`px-3 py-1.5 text-xs border transition ${
                statusFilter === "all"
                  ? "bg-[var(--primary)] text-[var(--inverse)] border-[var(--primary)]"
                  : "border-[var(--border)] hover:border-[var(--primary)]"
              }`}
              style={{ textTransform: "none", letterSpacing: "0" }}
              data-testid="filter-status-all"
            >
              Hamısı <span className="opacity-60">({counts.all || 0})</span>
            </button>
            {STATUS_CHIPS.map(([s, label]) => {
              const n = counts[s] || 0;
              if (n === 0) return null;
              return (
                <button
                  key={s}
                  onClick={() => setStatusFilter(s)}
                  className={`px-3 py-1.5 text-xs border transition ${
                    statusFilter === s
                      ? "bg-[var(--primary)] text-[var(--inverse)] border-[var(--primary)]"
                      : "border-[var(--border)] hover:border-[var(--primary)]"
                  }`}
                  style={{ textTransform: "none", letterSpacing: "0" }}
                  data-testid={`filter-status-${s}`}
                >
                  {label} <span className="opacity-60">({n})</span>
                </button>
              );
            })}
          </div>

          <div className="flex items-center gap-2 flex-wrap">
            <div className="flex items-center gap-2 border border-[var(--border)] px-3 py-1.5 flex-1 min-w-[240px] bg-[var(--bg)]">
              <Search size={14} className="text-[var(--text-muted)]" />
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Sifariş nömrəsi və ya məhsul axtar…"
                className="flex-1 bg-transparent outline-none text-sm"
                data-testid="orders-search"
              />
              {search && (
                <button onClick={() => setSearch("")} className="text-[var(--text-muted)]" aria-label="Təmizlə">
                  <X size={14} />
                </button>
              )}
            </div>
            <select
              value={sortKey}
              onChange={(e) => setSortKey(e.target.value)}
              className="bg-transparent border border-[var(--border)] px-3 py-1.5 text-sm"
              data-testid="orders-sort"
              aria-label="Sıralama"
            >
              {SORT_OPTIONS.map(([k]) => (
                <option key={k} value={k}>{sortLabel(k)}</option>
              ))}
            </select>
          </div>
        </div>
      )}

      {/* ── Sifariş siyahısı ────────────────────────────────────── */}
      <div className="mt-8 space-y-5">
        {loading && (
          <div className="text-[var(--text-2)] py-12 text-center">{sc(t.common.loading)}</div>
        )}

        {!loading && orders.length === 0 && (
          <div className="border border-[var(--border)] py-20 text-center">
            <Package size={36} strokeWidth={1.2} className="mx-auto text-[var(--text-muted)]" />
            <div className="font-display text-2xl mt-5">
              {tc(t.profile?.noOrders) || "Hələ sifariş yoxdur"}
            </div>
            <p className="text-sm text-[var(--text-2)] mt-2 max-w-md mx-auto leading-relaxed">
              İlk sifarişiniz buraya əlavə olunacaq. Kataloqdan başlayın.
            </p>
            <button onClick={() => nav("/catalog")} className="btn-primary mt-6" style={{ textTransform: "none", letterSpacing: "0" }}>
              Kataloqa keç
            </button>
          </div>
        )}

        {!loading && orders.length > 0 && visibleOrders.length === 0 && (
          <div className="border border-[var(--border)] py-16 text-center">
            <div className="text-[var(--text-muted)]">Filtrə uyğun sifariş yoxdur</div>
            <button
              onClick={() => { setStatusFilter("all"); setSearch(""); }}
              className="btn-outline mt-4"
              style={{ textTransform: "none", letterSpacing: "0" }}
              data-testid="orders-clear-filters"
            >
              Filtrləri təmizlə
            </button>
          </div>
        )}

        {visibleOrders.map((o) => (
          <OrderCard
            key={o.id}
            order={o}
            t={t}
            formatPrice={formatPrice}
          />
        ))}
      </div>
    </div>
  );
}

function StatBlock({ icon: Icon, label, value, accent = false, mono = false }) {
  return (
    <div className={`border p-4 ${accent ? "border-[var(--primary)] bg-[var(--surface)]" : "border-[var(--border)] bg-[var(--surface)]/30"}`}>
      <div className="flex items-center justify-between">
        <span className={labelCls}>{label}</span>
        {Icon && <Icon size={14} className={accent ? "text-[var(--primary)]" : "text-[var(--text-muted)]"} />}
      </div>
      <div className={`mt-2 ${mono ? "font-mono text-2xl" : "font-display text-3xl"}`}>{value}</div>
    </div>
  );
}
